import { CreateLogger, notice, warn } from "@swivl-mono/helpers";
import { APIServiceConfigInterface, Log } from "@swivl-mono/types";

import { FMSServiceProviderInterface } from "../APIs/FMS.API";

const log = CreateLogger(Log.API, "service");

export interface APIServiceConfig {
  API?: APIServiceConfigInterface;
  FMS?: APIServiceConfigInterface;
  Sync?: APIServiceConfigInterface;
  Messaging?: APIServiceConfigInterface;
  LegacyServer?: APIServiceConfigInterface;
  Bull?: APIServiceConfigInterface;
  Analytics?: APIServiceConfigInterface;
}

export class APIService {
  serverURL: string;
  tokenType: string;
  context: string;
  static Server: APIService;
  static FMS: APIService;
  static Sync: APIService;
  static Messaging: APIService;
  static Channels: APIService;
  static Notifications: APIService;
  static LegacyServer: APIService;
  static Bull: APIService;
  static Analytics: APIService;
  static FMSServiceProvider: FMSServiceProviderInterface;

  authCallBack: () => string = () => "unset";
  errorCallback: <T>(e: Error, method?: string, url?: string) => Promise<T> = (e) => {
    throw new Error(e.message);
  };

  static log() {
    ["Server", "FMS", "Sync", "Messaging", "Channels", "LegacyServer", "Bull"].forEach((service: any) => {
      if ((APIService as any)[service]) {
        warn(service, APIService as any);
      } else {
        warn(service, "Not Set");
      }
    });
  }

  static init(config: {
    Server?: APIServiceConfigInterface;
    FMS?: APIServiceConfigInterface;
    Sync?: APIServiceConfigInterface;
    Messaging?: APIServiceConfigInterface;
    Channels?: APIServiceConfigInterface;
    Notifications?: APIServiceConfigInterface;

    LegacyServer?: APIServiceConfigInterface;
    Bull?: APIServiceConfigInterface;
    Analytics?: APIServiceConfigInterface;
    FMSServiceProvider?: FMSServiceProviderInterface;
  }) {
    let APIServicesEnabled = "API Services Enabled:";
    if (config.Server) {
      APIServicesEnabled += " Server";
      APIService.Server = new APIService(config.Server);
    }
    if (config.FMS) {
      APIServicesEnabled += " FMS";
      APIService.FMS = new APIService(config.FMS);
    }
    if (config.Sync) {
      APIServicesEnabled += " Sync";
      APIService.Sync = new APIService(config.Sync);
    }
    if (config.Messaging) {
      APIServicesEnabled += " Messaging";
      APIService.Messaging = new APIService(config.Messaging);
    }
    if (config.Channels) {
      APIServicesEnabled += " Channels";
      APIService.Channels = new APIService(config.Channels);
    }
    if (config.LegacyServer) {
      APIServicesEnabled += " LegacyServer";
      APIService.LegacyServer = new APIService(config.LegacyServer);
    }
    if (config.Bull) {
      APIServicesEnabled += " Bull";
      APIService.Bull = new APIService(config.Bull);
    }
    if (config.Analytics) {
      APIServicesEnabled += " Analytics";
      APIService.Analytics = new APIService(config.Analytics);
    }
    if (config.FMSServiceProvider) {
      APIServicesEnabled += " FMSServiceProvider";
      APIService.FMSServiceProvider = config.FMSServiceProvider;
    }
    if (config.Notifications) {
      APIServicesEnabled += " Notifications";
      APIService.Notifications = new APIService(config.Notifications);
    }
    notice(APIServicesEnabled);
  }

  constructor(config: APIServiceConfigInterface) {
    this.serverURL = config.url;
    this.authCallBack = config.authCallBack;
    this.errorCallback = config.errorCallback;
    this.tokenType = config.tokenType || "api";
    this.context = config.context || "unknown";
  }

  private async sendWithBody<T>(method: "POST" | "PUT" | "PATCH", endpoint: string, body?: any) {
    const payload: any = {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, application/xml, text/xml",
        "x-access-token": this.getAuth(),
        "x-token-type": this.tokenType,
      },
    };

    const url = `${this.serverURL}/api${endpoint}`;

    if (body) {
      try {
        payload.body = JSON.stringify(body);
      } catch (e) {
        log("Error in stringify", e);
        return this.errorCatch<T>(e as Error, url, method);
      }
    }

    try {
      const response = await fetch(url, payload);

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message);
        } catch (e) {
          throw new Error(`HTTP error ${response.status}: ${errorText}`);
        }
      }

      if (response.status === 204) {
        return null as T;
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("xml")) {
        // Handle as XML
        const data = await response.text();
        return data as T;
      } else {
        // Handle as JSON
        const data = await response.json();
        return data as T;
      }
    } catch (e) {
      console.warn("Error in sendWithBody", body);
      return this.errorCatch<T>(e as Error, url, method);
    }
  }

  // TODO: headers param: temporary until we get fms service moved over to mono
  async get<T>(endpoint: string, params?: any, headers?: any): Promise<T> {
    const queryString = params ? "?" + new URLSearchParams(params).toString() : "";

    const url = `${this.serverURL}/api${endpoint}${queryString}`;

    const defaultHeaders: { [key: string]: string } = {
      "Content-Type": "application/json",
      "x-access-token": this.getAuth(),
      "x-token-type": this.tokenType,
    };

    // TODO: temporary until we get fms service moved over to mono
    const combinedHeaders = { ...defaultHeaders, ...headers };

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: combinedHeaders,
      });

      //NOTE - 204 is a no content response (null)
      if (response.status === 204) {
        return null as T;
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      if (response.status === 204) {
        return null as T;
      }

      const data = await response.json();
      return data as T;
    } catch (e) {
      return this.errorCatch<T>(e as Error, url, "GET");
    }
  }

  async delete<T>(endpoint: string, params?: any, body?: any): Promise<T> {
    const queryString = params ? "?" + new URLSearchParams(params).toString() : "";

    const url = `${this.serverURL}/api${endpoint}${queryString}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": this.getAuth(),
          "x-token-type": this.tokenType,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      if (response.status === 204) {
        return null as T;
      }

      const data = await response.json();
      return data as T;
    } catch (e) {
      return this.errorCatch<T>(e as Error, url, "DELETE");
    }
  }

  async put<T>(endpoint: string, body?: any) {
    return this.sendWithBody<T>("PUT", endpoint, body);
  }

  async patch<T>(endpoint: string, body?: any) {
    return this.sendWithBody<T>("PATCH", endpoint, body);
  }

  async post<T>(endpoint: string, body?: any, params?: any) {
    const queryString = params ? "?" + new URLSearchParams(params).toString() : "";
    endpoint += queryString;
    return this.sendWithBody<T>("POST", endpoint, body);
  }

  errorCatch<T>(error: Error, url: string, method: string): Promise<T> {
    if (this.errorCallback) {
      return this.errorCallback<T>(error, method, url);
    }

    throw new Error(error.message);
  }

  getAuth() {
    if (!this.authCallBack) {
      throw new Error("No Auth Callback Set");
    }
    const auth = this.authCallBack();

    if (auth === "unset") {
      throw new Error("No Auth Callback Set mk2");
    }
    return auth;
  }
}
